import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  Eye,
  EyeOff,
  AlertCircle,
  ArrowRight,
  MessageCircle,
  CheckCircle2,
  Zap,
  BarChart3,
  ShoppingBag,
  CreditCard,
  Package,
  Users,
  Smartphone,
  Monitor,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { apiFetch } from '@/lib/api';

// --- Logo Component ---

function FluxoLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 40 40" fill="none" className={className}>
      <rect width="40" height="40" rx="10" fill="#1E3A5F" />
      <path d="M12 10h16v4H16v6h10v4H16v10h-4V10z" fill="white" />
      <path
        d="M20 24c4 0 7 2 9 5s3 6 3 9"
        stroke="#10B981"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// --- Animated Section Wrapper ---

function FadeInSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}


// --- Feature Data ---

const features = [
  {
    icon: Zap,
    title: 'PDV Rápido',
    desc: 'Venda em segundos com leitor de código de barras. Funciona offline.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=80',
  },
  {
    icon: Package,
    title: 'Estoque Inteligente',
    desc: 'Alertas automáticos, controle de validade e etiquetas.',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&q=80',
  },
  {
    icon: CreditCard,
    title: 'Crédito Controlado',
    desc: 'Limite de crédito, vencimento e cobrança via WhatsApp.',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80',
  },
  {
    icon: BarChart3,
    title: 'Relatórios em PDF',
    desc: 'Vendas, estoque e financeiro com exportação profissional.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&q=80',
  },
  {
    icon: Smartphone,
    title: 'Loja Online',
    desc: 'Seus clientes pedem pelo celular e você entrega.',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&q=80',
  },
  {
    icon: Users,
    title: 'Multi-caixa',
    desc: 'Vários operadores, cada um com seu login e permissões.',
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=400&q=80',
  },
];


// --- Main Component ---

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [modal, setModal] = React.useState<'none' | 'login' | 'register' | 'success'>('none');
  const [loginName, setLoginName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [storeCode, setStoreCode] = React.useState(localStorage.getItem('peg_e_pag_store_code') || '');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(true);
  const [error, setError] = React.useState('');

  const [regData, setRegData] = React.useState({
    nomeFantasia: '',
    cnpj: '',
    telefone: '',
    email: '',
    cidade: '',
    endereco: '',
    responsavel: '',
    plano: 'completo',
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      const storedUser = JSON.parse(localStorage.getItem('peg_e_pag_user') || '{}');
      if (storedUser?.role === 'operador_caixa') {
        navigate('/pdv', { replace: true });
      } else {
        navigate('/admin/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  async function handleLogin(ev: React.FormEvent) {
    ev.preventDefault();
    setError('');
    if (!loginName.trim() || !password) {
      setError('Preencha todos os campos');
      return;
    }
    setIsSubmitting(true);
    try {
      if (rememberMe) { localStorage.setItem('peg_e_pag_store_code', storeCode.trim()); }
      await login(loginName.trim(), password, storeCode.trim() || undefined);
      toast.success('Bem-vindo!');
      const storedUser = JSON.parse(localStorage.getItem('peg_e_pag_user') || '{}');
      if (storedUser?.role === 'operador_caixa') {
        navigate('/pdv', { replace: true });
      } else {
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Credenciais inválidas');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRegister(ev: React.FormEvent) {
    ev.preventDefault();
    if (!regData.nomeFantasia || !regData.telefone || !regData.email) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiFetch('/api/registration-requests', {
        method: 'POST',
        body: JSON.stringify({
          nomeFantasia: regData.nomeFantasia,
          cnpj: regData.cnpj,
          telefone: regData.telefone,
          email: regData.email,
          plano: regData.plano,
          cidade: regData.cidade,
          endereco: regData.endereco,
          responsavel: regData.responsavel,
        }),
      }).catch(() => {});
      setModal('success');
    } catch {
      toast.error('Erro ao enviar');
    } finally {
      setIsSubmitting(false);
    }
  }


  return (
    <div className="min-h-screen bg-background">
      {/* --- NAVBAR (Sticky Glass) --- */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/30 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <FluxoLogo className="h-9 w-9" />
            <span className="text-lg font-bold text-foreground tracking-tight">FluxoPDV</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <a href="#funcionalidades" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Funcionalidades
            </a>
            <a href="#planos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Planos
            </a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setModal('login')}
              className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Entrar
            </button>
            <button
              onClick={() => setModal('register')}
              className="rounded-lg bg-gradient-to-r from-secondary to-primary px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30 hover:opacity-90"
            >
              Começar Grátis
              <ArrowRight className="inline-block ml-1.5 h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section className="relative min-h-screen flex items-center pt-16">
        {/* Background effects */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 h-[500px] w-[500px] rounded-full bg-secondary/20 blur-[120px]" />
          <div className="absolute bottom-1/4 -right-32 h-[400px] w-[400px] rounded-full bg-primary/10 blur-[100px]" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
        </div>

        <div className="relative mx-auto max-w-7xl w-full px-5 lg:px-8 py-20">
          <div className="grid lg:grid-cols-[55%_45%] gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 mb-6">
                <span className="text-sm">&#10024;</span>
                <span className="text-sm text-primary font-medium">14 dias grátis - Sem cartão de crédito</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.08] tracking-tight">
                Seu comércio merece um sistema que{' '}
                <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  funciona de verdade
                </span>
              </h1>

              {/* Subtitle */}
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
                PDV completo, controle de estoque, crédito, fidelidade e relatórios. Tudo num sistema bonito que roda até offline.
              </p>

              {/* CTA Buttons */}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => setModal('register')}
                  className="rounded-xl bg-gradient-to-r from-secondary to-primary px-8 py-4 text-base font-semibold text-white shadow-xl shadow-primary/20 transition-all hover:shadow-primary/30 hover:opacity-90 hover:scale-[1.02]"
                >
                  Começar grátis
                </button>
                <button
                  onClick={() => setModal('login')}
                  className="flex items-center gap-2 rounded-xl border border-border px-7 py-4 text-base font-medium text-foreground transition-all hover:border-primary/40 hover:bg-primary/5"
                >
                  <Monitor className="h-4.5 w-4.5" />
                  Ver demonstração
                </button>
              </div>

              {/* Trust note */}
              <p className="mt-10 text-sm text-muted-foreground/70">
                &#10003; Sem fidelidade &nbsp; &#10003; Suporte via WhatsApp &nbsp; &#10003; Funciona offline
              </p>
            </motion.div>

            {/* Right Side - Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/40">
                <img
                  src="https://images.unsplash.com/photo-1578916171728-46686eac8d58?w=800&q=80"
                  alt="Comerciante no balcao do seu estabelecimento"
                  loading="lazy"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-transparent" />
              </div>

              {/* Floating Card - Sale Total */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="absolute -left-6 bottom-24 rounded-xl border border-border bg-card/95 backdrop-blur-md p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <ShoppingBag className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground/70">Venda #1847</p>
                    <p className="text-lg font-bold text-foreground">R$ 127,50</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card - Stock Alert */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.0 }}
                className="absolute -right-4 top-16 rounded-xl border border-border bg-card/95 backdrop-blur-md p-3 shadow-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                  <p className="text-xs text-muted-foreground">Estoque baixo: <span className="text-foreground font-medium">Leite Integral</span></p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* --- NUMBERS / TRUST SECTION --- */}
      <section className="relative py-16 border-y border-border/30">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <FadeInSection>
            <div className="text-center">
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Sistema desenvolvido para o pequeno e médio comércio brasileiro. Mercadinhos, padarias, mercearias, conveniências.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* --- FEATURES SECTION --- */}
      <section id="funcionalidades" className="py-24 px-5 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <FadeInSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                Tudo que seu comércio precisa
              </h2>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                Funcionalidades pensadas para o dia a dia de mercadinhos, padarias e mercearias
              </p>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <FadeInSection key={f.title} delay={i * 0.1}>
                <div className="group relative rounded-2xl border border-border bg-card overflow-hidden transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5">
                  {/* Feature Image */}
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={f.image}
                      alt={f.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                  </div>

                  {/* Feature Content */}
                  <div className="p-6 pt-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                        <f.icon className="h-4.5 w-4.5 text-primary" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground">{f.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* --- SCREENSHOT / MOCKUP SECTION --- */}
      <section className="py-24 px-5 lg:px-8 relative overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-60" />

        <div className="relative mx-auto max-w-7xl">
          <FadeInSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                Interface pensada para o dia a dia do comerciante
              </h2>
              <p className="mt-4 text-muted-foreground">
                Simples de usar, bonito de ver, rápido de operar
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.2}>
            {/* Browser Mockup */}
            <div className="mx-auto max-w-5xl">
              <div className="rounded-xl border border-border bg-card shadow-2xl shadow-black/40 overflow-hidden">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card">
                  <div className="flex gap-1.5">
                    <div className="h-3 w-3 rounded-full bg-[#FF5F57]" />
                    <div className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
                    <div className="h-3 w-3 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="mx-auto max-w-md rounded-md bg-muted px-4 py-1.5 text-center">
                      <span className="text-xs text-muted-foreground/70">app.fluxopdv.com.br/pdv</span>
                    </div>
                  </div>
                </div>

                {/* Screenshot */}
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80"
                    alt="Pessoa usando sistema no computador"
                    loading="lazy"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card/20 to-transparent" />
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>


      {/* --- PRICING SECTION --- */}
      <section id="planos" className="py-24 px-5 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <FadeInSection>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                Planos que cabem no seu bolso
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                14 dias grátis em qualquer plano. Cancele quando quiser.
              </p>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              {
                name: 'Essencial',
                price: '100',
                features: ['PDV completo', 'Controle de estoque', 'Cadastro de clientes', 'Relatórios básicos', 'Modo offline', '1 caixa', 'Suporte por WhatsApp'],
              },
              {
                name: 'Completo',
                price: '200',
                popular: true,
                features: ['Tudo do Essencial', 'Programa de fidelidade', 'Relatórios DRE', 'Cobrança via WhatsApp', 'Loja online com entrega', 'Caixas ilimitados', 'Backup na nuvem', 'Suporte prioritário'],
              },
            ].map((plan, i) => (
              <FadeInSection key={plan.name} delay={i * 0.1}>
                <div
                  className={`relative rounded-2xl border p-7 transition-all ${
                    plan.popular
                      ? 'border-primary/50 bg-gradient-to-b from-primary/5 to-card shadow-lg shadow-primary/10'
                      : 'border-border bg-card hover:border-border/80'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-secondary to-primary px-4 py-1">
                      <span className="text-xs font-bold text-white">MAIS POPULAR</span>
                    </div>
                  )}

                  <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-foreground">R${plan.price}</span>
                    <span className="text-sm text-muted-foreground/70">/mês</span>
                  </div>

                  <ul className="mt-6 space-y-3">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => setModal('register')}
                    className={`mt-8 w-full rounded-xl py-3 text-sm font-semibold transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-secondary to-primary text-white shadow-lg shadow-primary/20 hover:opacity-90'
                        : 'border border-border text-foreground hover:border-primary/40 hover:bg-primary/5'
                    }`}
                  >
                    Começar grátis
                  </button>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section className="py-24 px-5 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <FadeInSection>
            <div className="relative rounded-3xl border border-border bg-gradient-to-br from-card to-secondary/20 p-12 sm:p-16 text-center overflow-hidden">
              {/* Background glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[300px] w-[600px] rounded-full bg-primary/5 blur-[100px]" />

              <div className="relative">
                <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
                  Pronto para simplificar seu comércio?
                </h2>
                <p className="mt-4 text-lg text-muted-foreground">
                  Sem compromisso. Cancele quando quiser.
                </p>
                <button
                  onClick={() => setModal('register')}
                  className="mt-8 rounded-xl bg-gradient-to-r from-secondary to-primary px-10 py-4 text-base font-semibold text-white shadow-xl shadow-primary/20 transition-all hover:shadow-primary/30 hover:opacity-90 hover:scale-[1.02]"
                >
                  Começar grátis - 14 dias
                </button>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>


      {/* --- FOOTER --- */}
      <footer className="border-t border-border/30 px-5 lg:px-8 py-12">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <FluxoLogo className="h-8 w-8" />
                <span className="text-base font-bold text-foreground">FluxoPDV</span>
              </div>
              <p className="text-sm text-muted-foreground/70 leading-relaxed">
                Gestão comercial que flui. Sistema completo para mercadinhos, padarias e mercearias.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">Links</h4>
              <ul className="space-y-2.5">
                <li>
                  <a href="#funcionalidades" className="text-sm text-muted-foreground/70 hover:text-primary transition-colors">
                    Funcionalidades
                  </a>
                </li>
                <li>
                  <a href="#planos" className="text-sm text-muted-foreground/70 hover:text-primary transition-colors">
                    Planos
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/5589981519936" target="_blank" rel="noopener" className="text-sm text-muted-foreground/70 hover:text-primary transition-colors">
                    Suporte
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">Contato</h4>
              <div className="space-y-2.5">
                <a
                  href="https://wa.me/5589981519936"
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-2 text-sm text-muted-foreground/70 hover:text-primary transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  (89) 98151-9936
                </a>
                <a
                  href="mailto:nilsom.neto@gmail.com"
                  className="text-sm text-muted-foreground/70 hover:text-primary transition-colors"
                >
                  nilsom.neto@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-10 pt-8 border-t border-border/30 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-sm text-muted-foreground/70">Nilsom Neto - Análise e Desenvolvimento de Sistemas</p>
              <p className="text-xs text-muted-foreground/70 mt-1">CNPJ 57.255.294/0001-10</p>
            </div>
            <p className="text-xs text-muted-foreground/70">
              &copy; {new Date().getFullYear()} FluxoPDV. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>


      {/* --- MODALS --- */}
      <AnimatePresence>
        {modal !== 'none' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => { if (modal !== 'success') setModal('none'); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-[420px] rounded-2xl border border-border bg-card p-7 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              {modal !== 'success' && (
                <button
                  onClick={() => setModal('none')}
                  className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground/70 hover:text-foreground transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}

              {/* LOGIN MODAL */}
              {modal === 'login' && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <FluxoLogo className="h-8 w-8" />
                    <div>
                      <h2 className="text-xl font-bold text-foreground">Entrar</h2>
                      <p className="text-sm text-muted-foreground/70">Acesse o painel do seu estabelecimento</p>
                    </div>
                  </div>

                  {error && (
                    <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-500/8 border border-red-500/15 px-3 py-2.5 text-sm text-red-400">
                      <AlertCircle className="h-4 w-4 shrink-0" />{error}
                    </div>
                  )}

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Código da Loja</label>
                      <input
                        type="text"
                        value={storeCode}
                        onChange={(e) => setStoreCode(e.target.value)}
                        placeholder="Ex: mercadinho-joao"
                        autoComplete="organization"
                        className="h-11 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                      />
                      <span className="mt-1 block text-[11px] text-muted-foreground/70">Deixe em branco para busca automática</span>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Usuário</label>
                      <input
                        type="text"
                        value={loginName}
                        onChange={(e) => setLoginName(e.target.value)}
                        placeholder="Seu login"
                        autoComplete="username"
                        className="h-11 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Senha</label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className="h-11 w-full rounded-xl border border-border bg-background px-4 pr-11 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary/40"
                      />
                      <label htmlFor="rememberMe" className="text-[13px] text-muted-foreground cursor-pointer">
                        Manter conectado
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="h-11 w-full rounded-xl bg-gradient-to-r from-secondary to-primary text-sm font-semibold text-white shadow-lg shadow-primary/20 hover:opacity-90 disabled:opacity-50 transition-all"
                    >
                      {isSubmitting ? 'Entrando...' : 'Entrar'}
                    </button>
                  </form>

                  <p className="mt-5 text-center text-sm text-muted-foreground/70">
                    Não tem conta?{' '}
                    <button onClick={() => { setModal('register'); setError(''); }} className="text-primary hover:underline font-medium">
                      Criar conta
                    </button>
                  </p>
                </>
              )}


              {/* REGISTER MODAL */}
              {modal === 'register' && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <FluxoLogo className="h-8 w-8" />
                    <div>
                      <h2 className="text-xl font-bold text-foreground">Criar conta</h2>
                      <p className="text-sm text-muted-foreground/70">Liberamos seu acesso em até 24h</p>
                    </div>
                  </div>

                  <form onSubmit={handleRegister} className="space-y-3.5">
                    <div>
                      <label className="mb-1 block text-[13px] font-medium text-muted-foreground">Nome do Estabelecimento *</label>
                      <input
                        type="text"
                        value={regData.nomeFantasia}
                        onChange={(e) => setRegData({ ...regData, nomeFantasia: e.target.value })}
                        placeholder="Ex: Mercado Bom Preco"
                        required
                        className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[13px] font-medium text-muted-foreground">Nome do Responsável *</label>
                      <input
                        type="text"
                        value={regData.responsavel}
                        onChange={(e) => setRegData({ ...regData, responsavel: e.target.value })}
                        placeholder="Seu nome completo"
                        required
                        className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-[13px] font-medium text-muted-foreground">WhatsApp *</label>
                        <input
                          type="text"
                          value={regData.telefone}
                          onChange={(e) => setRegData({ ...regData, telefone: e.target.value })}
                          placeholder="(00) 00000-0000"
                          required
                          className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[13px] font-medium text-muted-foreground">CNPJ</label>
                        <input
                          type="text"
                          value={regData.cnpj}
                          onChange={(e) => setRegData({ ...regData, cnpj: e.target.value })}
                          placeholder="Opcional"
                          className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-[13px] font-medium text-muted-foreground">E-mail *</label>
                      <input
                        type="email"
                        value={regData.email}
                        onChange={(e) => setRegData({ ...regData, email: e.target.value })}
                        placeholder="seu@email.com"
                        required
                        className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[13px] font-medium text-muted-foreground">Cidade / Estado *</label>
                      <input
                        type="text"
                        value={regData.cidade}
                        onChange={(e) => setRegData({ ...regData, cidade: e.target.value })}
                        placeholder="Ex: Teresina - PI"
                        required
                        className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-[13px] font-medium text-muted-foreground">Endereço</label>
                      <input
                        type="text"
                        value={regData.endereco}
                        onChange={(e) => setRegData({ ...regData, endereco: e.target.value })}
                        placeholder="Rua, número, bairro"
                        className="h-10 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-transparent transition-all"
                      />
                    </div>

                    {/* Plan Selection */}
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      {[
                        { id: 'essencial', name: 'Essencial', price: '100' },
                        { id: 'completo', name: 'Completo', price: '200', rec: true },
                      ].map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setRegData({ ...regData, plano: p.id })}
                          className={`relative rounded-xl border p-3 text-center transition-all ${
                            regData.plano === p.id
                              ? 'border-primary bg-primary/8'
                              : 'border-border hover:border-border/80'
                          }`}
                        >
                          {p.rec && (
                            <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded bg-gradient-to-r from-secondary to-primary px-2 py-0.5 text-[9px] font-bold text-white">
                              RECOMENDADO
                            </span>
                          )}
                          <p className="text-sm font-semibold text-foreground">{p.name}</p>
                          <p className="text-xs text-muted-foreground/70">R${p.price}/mês</p>
                        </button>
                      ))}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="h-11 w-full rounded-xl bg-gradient-to-r from-secondary to-primary text-sm font-semibold text-white shadow-lg shadow-primary/20 hover:opacity-90 disabled:opacity-50 transition-all"
                    >
                      {isSubmitting ? 'Enviando...' : 'Solicitar acesso - 14 dias grátis'}
                    </button>
                  </form>

                  <p className="mt-4 text-center text-sm text-muted-foreground/70">
                    Já tem conta?{' '}
                    <button onClick={() => setModal('login')} className="text-primary hover:underline font-medium">
                      Entrar
                    </button>
                  </p>
                </>
              )}

              {/* SUCCESS MODAL */}
              {modal === 'success' && (
                <div className="text-center py-4">
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                    <CheckCircle2 className="h-7 w-7 text-primary" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground mb-2">Solicitação enviada!</h2>
                  <p className="text-sm text-muted-foreground mb-5">
                    Vamos entrar em contato pelo WhatsApp em até 24h para liberar seu acesso.
                  </p>
                  <a
                    href="https://wa.me/5589981519936"
                    target="_blank"
                    rel="noopener"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:opacity-90 mb-4"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Falar agora no WhatsApp
                  </a>
                  <br />
                  <button
                    onClick={() => setModal('none')}
                    className="mt-2 text-sm text-muted-foreground/70 hover:text-foreground transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
